import React from 'react';
import {useNavigate} from 'react-router-dom';

import {AlertActions, AlertModal} from 'presentation/components';
import {HealthBookingEnum, QueryParamsEnum} from 'presentation/constants';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {FlowEnum} from '../../constants';
import {getChangeContent, ChangeFlowVariantEnum} from './constants';
import {ChangeFlowDialogProps} from './types';
import {LOCATION_ROUTE} from 'presentation/modules/booking/shared/constants/bookingLocationPath';
import {useResetBookingFlow} from 'presentation/modules/booking/shared/hooks/useResetBookingFlow';

const {cancel, confirm, notice, confirmProceed} = translations['pt-br'].commons;

export function ChangeFlowDialog({
	open,
	setOpen,
	setActiveBookingFlowStep,
	flowType,
	variant,
	modalDataTagId,
	cancelModalDataTagId,
	confirmModalDataTagId,
	closeModalDataTagId,
}: ChangeFlowDialogProps) {
	const {isManualLocation, isFavoriteDoctorsFlow} = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isManualLocationHealthExamOdonto =
		isManualLocation && (isHealthFlow || isExamFlow || isOdontoFlow);
	const {LOCATION_STEP, SPECIALITY_OR_EXAM_STEP, UNIT_STEP} = getStepPosition({
		flowType,
		isManualLocation,
	});

	const {setSearchParams, clearSearch} = useQueryParams();
	const navigate = useNavigate();
	const {resetBookingFlow} = useResetBookingFlow();

	const handleClose = () => {
		setOpen(false);
	};

	const handleConfirm = () => {
		setOpen(false);
		clearSearch();

		if (variant === ChangeFlowVariantEnum.LOCATION && setActiveBookingFlowStep) {
			resetBookingFlow();

			if (isManualLocationHealthExamOdonto) {
				setActiveBookingFlowStep(LOCATION_STEP);
				return;
			}
			navigate(LOCATION_ROUTE[flowType], {replace: true});
			setActiveBookingFlowStep(LOCATION_STEP);
			return;
		}

		if (variant === ChangeFlowVariantEnum.SPECIALTY && setActiveBookingFlowStep) {
			resetBookingFlow({keepGeolocationInfo: true, keepTypeLocalName: true});

			setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
			return;
		}

		if (variant === ChangeFlowVariantEnum.UNIT && setActiveBookingFlowStep) {
			setActiveBookingFlowStep(UNIT_STEP);

			setBookingData(prev => ({
				...prev,
				professionalCode: isFavoriteDoctorsFlow ? prev.professionalCode : '',
				professionalName: isFavoriteDoctorsFlow ? prev.professionalName : '',
				dateAt: '',
				hourAt: '',
				unitCode: '',
				unitName: '',
				unitAddress: '',
				odontoRecommendations: '',
				districtName: isManualLocationHealthExamOdonto ? prev.districtName : '',
				odontoUnitDistrictSelected: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.SEARCH_QUERY);
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);
					prev.delete(QueryParamsEnum.DISABILITY);
					prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);

					return prev;
				},
				{
					replace: true,
				},
			);

			if (flowType === FlowEnum.TELEHEALTH) {
				navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
			}
		}
	};

	return (
		<AlertModal
			headerDataTagId={modalDataTagId}
			closeBtnDataTagId={closeModalDataTagId}
			footerContent={
				<AlertActions
					primaryAction={handleConfirm}
					secondaryAction={handleClose}
					secondaryText={cancel}
					primaryText={confirm}
					dataTagId={confirmModalDataTagId}
					secondaryBtnDataTagId={cancelModalDataTagId}
				/>
			}
			description={getChangeContent({variant, isExamFlow}).description}
			headerTitle={notice}
			onClose={() => setOpen(false)}
			title={confirmProceed}
			open={open}
			variant="primary"
		/>
	);
}
